import React, { useState, useEffect, useRef } from 'react';
import { X, User, Calendar, FileText, Repeat, Upload, XCircle } from 'lucide-react';
import { useTask } from '../../contexts/TaskContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { turkishIncludes } from '../../utils/turkishUtils';

const TaskAssignmentModal = ({ onClose, onSuccess }) => {
  const { createTask } = useTask();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [openUserDropdown, setOpenUserDropdown] = useState(false);
  const userDropdownRef = useRef(null);

  // Form state
  const [assignedToId, setAssignedToId] = useState('');
  const [selectedUserName, setSelectedUserName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('daily');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [files, setFiles] = useState([]);

  // Kullanıcıları yükle
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await api.get('/api/users/assignable');
        if (response.data.success) {
          const usersData = response.data.data || [];
          setUsers(usersData);
          setFilteredUsers(usersData);
        }
      } catch (error) {
        console.error('Kullanıcılar yüklenemedi:', error);
        toast.error('Kullanıcılar yüklenirken hata oluştu');
      }
    };
    loadUsers();
  }, []);

  // Kullanıcı arama filtresi
  useEffect(() => {
    if (!userSearch.trim()) {
      setFilteredUsers(users);
      return;
    }
    const filtered = users.filter(user => {
      const username = user.username || '';
      return turkishIncludes(username, userSearch);
    });
    setFilteredUsers(filtered);
  }, [userSearch, users]);

  // Dropdown dışına tıklama
  useEffect(() => {
    const handler = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setOpenUserDropdown(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Dosya seçme
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  // Dosya silme
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Form gönderme
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!assignedToId || !title.trim()) {
      toast.error('Görev başlığı ve atanacak kullanıcı zorunludur');
      return;
    }

    try {
      setLoading(true);

      const taskData = {
        assignedToId: parseInt(assignedToId),
        title: title.trim(),
        description: description.trim() || null,
        dueDate: dueDate || null,
        isRecurring,
        recurrencePattern: isRecurring ? {
          type: recurrenceType,
          interval: recurrenceInterval
        } : null,
        files: files.length > 0 ? files : null
      };

      await createTask(taskData);
      toast.success('Görev başarıyla oluşturuldu');
      
      // Formu temizle
      setAssignedToId('');
      setSelectedUserName('');
      setTitle('');
      setDescription('');
      setDueDate('');
      setIsRecurring(false);
      setRecurrenceType('daily');
      setRecurrenceInterval(1);
      setFiles([]);
      setUserSearch('');

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Görev oluşturma hatası:', error);
      toast.error(error.response?.data?.message || 'Görev oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcı seçimi
  const selectUser = (user) => {
    setAssignedToId(String(user.id));
    setSelectedUserName(user.username || '');
    setUserSearch(user.username || '');
    setOpenUserDropdown(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal Panel */}
        <div
          className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Yeni Görev Ata
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-4">
            <div className="space-y-4">
              {/* Kullanıcı Seçimi */}
              <div className="relative" ref={userDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Kime Atanacak <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Kullanıcı ara..."
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    setOpenUserDropdown(true);
                    if (assignedToId && e.target.value !== selectedUserName) {
                      setAssignedToId('');
                      setSelectedUserName('');
                    }
                  }}
                  onFocus={() => setOpenUserDropdown(true)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {openUserDropdown && filteredUsers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => selectUser(user)}
                        className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-sm border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {user.username}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Görev Başlığı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Görev Başlığı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Görev başlığını girin..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              {/* Açıklama */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Açıklama
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Görev açıklamasını girin..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Tamamlanma Tarihi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Tamamlanma Tarihi
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Tekrarlama */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                  />
                  <Repeat className="w-4 h-4" />
                  Tekrarlayan Görev
                </label>
                {isRecurring && (
                  <div className="ml-6 mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Tekrar Tipi
                      </label>
                      <select
                        value={recurrenceType}
                        onChange={(e) => setRecurrenceType(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="daily">Günlük</option>
                        <option value="weekly">Haftalık</option>
                        <option value="monthly">Aylık</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Aralık
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={recurrenceInterval}
                        onChange={(e) => setRecurrenceInterval(parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Dosya Ekleme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Upload className="w-4 h-4 inline mr-1" />
                  Dosya Ekle
                </label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  multiple
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Oluşturuluyor...' : 'Görev Oluştur'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TaskAssignmentModal;

